import { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  X,
  Brain,
  Loader2,
  ArrowRight,
  ArrowLeft,
  RefreshCcw,
  Sparkles,
  ShieldAlert,
  HeartHandshake,
  Wind,
  BedDouble,
  Dumbbell,
  Leaf,
  NotebookPen,
  Smartphone,
  Users,
  Timer,
  Utensils,
  Check,
  Info,
  TrendingUp,
  TrendingDown,
  Minus,
  Phone,
  Stethoscope,
  Download,
} from "lucide-react";
import { toast } from "sonner";
import { useMental } from "@/context/MentalContext";
import { downloadMentalReportPDF } from "@/lib/pdfReport";
import { unlockAchievement } from "@/components/silentscan/GamificationSection";
import CopingBox from "@/components/silentscan/CopingBox";
import { useAuth } from "@/context/AuthContext";
import { saveRecord } from "@/lib/records";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_ICONS = {
  breathing: Wind,
  sleep: BedDouble,
  activity: Dumbbell,
  mindfulness: Leaf,
  journaling: NotebookPen,
  screen: Smartphone,
  social: Users,
  time: Timer,
  nutrition: Utensils,
};

const CONFIDENCE_STYLE = {
  Low: { color: "text-amber-400", border: "border-amber-400/30", bg: "bg-amber-500/10" },
  Medium: { color: "text-cyan-300", border: "border-cyan-400/30", bg: "bg-cyan-500/10" },
  High: { color: "text-emerald-400", border: "border-emerald-400/30", bg: "bg-emerald-500/10" },
};

const IMPACT_STYLE = {
  positive: { Icon: TrendingUp, color: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-400/30", label: "Positive" },
  negative: { Icon: TrendingDown, color: "text-rose-400", bg: "bg-rose-500/10", border: "border-rose-400/30", label: "Needs care" },
  neutral: { Icon: Minus, color: "text-zinc-400", bg: "bg-white/[0.04]", border: "border-white/10", label: "Neutral" },
};

export default function MentalWellnessDialog() {
  const { open, setOpen, closeMental } = useMental();
  const [starting, setStarting] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({}); // { id: value }
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [assessment, setAssessment] = useState(null);
  const [distress, setDistress] = useState(null);
  const [helplines, setHelplines] = useState([]);
  const [disclaimer, setDisclaimer] = useState("");

  const start = async () => {
    if (starting) return;
    setStarting(true);
    setAnswers({});
    setStep(0);
    setAssessment(null);
    setDistress(null);
    try {
      const { data } = await axios.post(`${API}/mental/start`);
      setSessionId(data.session_id);
      setQuestions(data.questions);
      setDisclaimer(data.disclaimer);
    } catch (e) {
      console.error(e);
      toast.error("Could not start the check-in.");
    } finally {
      setStarting(false);
    }
  };

  useEffect(() => {
    if (open && !sessionId) start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const total = questions.length;
  const q = questions[step];
  const currentValue = q ? answers[q.id] : undefined;
  const progressPct = total ? Math.round(((step + (currentValue ? 1 : 0)) / total) * 100) : 0;

  const setValue = (val) => {
    if (!q) return;
    setAnswers((a) => ({ ...a, [q.id]: String(val) }));
  };

  const next = () => {
    if (step < total - 1) setStep(step + 1);
  };

  const prev = () => {
    if (step > 0) setStep(step - 1);
  };

  const submit = async () => {
    if (submitting || !sessionId) return;
    setSubmitting(true);
    try {
      const payload = {
        session_id: sessionId,
        answers: Object.entries(answers).map(([id, value]) => ({ id, value })),
      };
      const { data } = await axios.post(`${API}/mental/complete`, payload);
      if (data.distress) {
        setDistress(data.distress_message);
        setHelplines(data.helplines || []);
      } else if (data.assessment) {
        setAssessment(data.assessment);
        unlockAchievement("mental_check", 50);
        toast.success("Your mental wellness insights are ready");
      }
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Could not analyze your responses.");
    } finally {
      setSubmitting(false);
    }
  };

  const restart = () => {
    setSessionId(null);
    setQuestions([]);
    setAnswers({});
    setStep(0);
    setAssessment(null);
    setDistress(null);
    setHelplines([]);
    start();
  };

  const answeredCount = Object.keys(answers).length;
  const isLastQ = step === total - 1;
  const canProceed = !!currentValue;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogPortal>
        <DialogOverlay className="bg-black/80 backdrop-blur-md z-50" />
        <DialogContent
          data-testid="mental-dialog"
          className="fixed left-[50%] top-[50%] z-50 w-[95vw] max-w-3xl h-[90dvh] max-h-[780px] -translate-x-1/2 -translate-y-1/2 p-0 border-0 bg-transparent shadow-none [&>button.absolute]:hidden"
        >
          <div className="relative w-full h-full overflow-hidden rounded-3xl glass-card border border-white/10 bg-[#05050A]/95 shadow-[0_40px_120px_rgba(0,229,255,0.25)] flex flex-col">
            <DialogTitle className="sr-only">Mental Wellness Check-in</DialogTitle>
            <DialogDescription className="sr-only">
              A short AI-powered mental wellness check-in that gives you a
              personalized Mental Wellness Score and gentle recommendations.
            </DialogDescription>

            {/* Header */}
            <div className="flex items-center justify-between px-5 sm:px-7 py-4 border-b border-white/5 bg-black/40 backdrop-blur-xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#0B3D91] to-cyan-500 flex items-center justify-center shadow-[0_0_20px_rgba(0,229,255,0.35)]">
                  <Brain className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="font-display text-base font-semibold">
                    Mental Wellness Check-in
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-cyan-400">
                    {assessment ? "Insights ready" : distress ? "Reaching out matters" : "AI Assistant · Gemini"}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {(assessment || distress) && (
                  <button
                    data-testid="mental-restart-btn"
                    onClick={restart}
                    className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs btn-outline-glass text-zinc-300"
                  >
                    <RefreshCcw className="w-3 h-3" />
                    Restart
                  </button>
                )}
                <button
                  data-testid="mental-close-btn"
                  onClick={closeMental}
                  className="w-8 h-8 rounded-full flex items-center justify-center bg-white/[0.04] border border-white/10 hover:border-cyan-400/40 transition-colors"
                  aria-label="Close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Progress */}
            {!assessment && !distress && (
              <div className="px-5 sm:px-7 py-3 bg-black/20">
                <div className="h-1 bg-white/5 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-[#0B3D91] to-cyan-400 transition-all duration-500"
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
                <div className="mt-1.5 flex justify-between text-[10px] text-zinc-500 uppercase tracking-[0.15em]">
                  <span>Question {Math.min(step + 1, total)} of {total || "…"}</span>
                  <span>{progressPct}%</span>
                </div>
              </div>
            )}

            {/* Body */}
            <div className="flex-1 overflow-hidden">
              {starting || !q ? (
                <div className="h-full flex items-center justify-center">
                  <Loader2 className="w-6 h-6 text-cyan-400 animate-spin" />
                </div>
              ) : distress ? (
                <DistressView message={distress} helplines={helplines} onRestart={restart} />
              ) : assessment ? (
                <ResultView assessment={assessment} />
              ) : (
                <QuestionView
                  q={q}
                  value={currentValue}
                  onChange={setValue}
                />
              )}
            </div>

            {/* Footer */}
            {!starting && q && !assessment && !distress && (
              <div className="border-t border-white/5 bg-black/40 backdrop-blur-xl px-5 sm:px-7 py-4 flex items-center justify-between gap-3">
                <button
                  data-testid="mental-prev-btn"
                  onClick={prev}
                  disabled={step === 0}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm btn-outline-glass text-zinc-300 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  Back
                </button>
                <div className="text-[11px] text-zinc-500">
                  {answeredCount}/{total} answered
                </div>
                {isLastQ ? (
                  <button
                    data-testid="mental-submit-btn"
                    onClick={submit}
                    disabled={submitting || !canProceed}
                    className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-medium btn-glow text-white disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        Analyzing…
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-3.5 h-3.5" />
                        Generate my insights
                      </>
                    )}
                  </button>
                ) : (
                  <button
                    data-testid="mental-next-btn"
                    onClick={next}
                    disabled={!canProceed}
                    className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-medium btn-glow text-white disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Next
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            )}

            {/* Bottom disclaimer strip on question view */}
            {!assessment && !distress && disclaimer && q && (
              <div className="px-5 sm:px-7 py-2 border-t border-white/5 bg-black/30">
                <div className="flex items-start gap-2 text-[10px] text-zinc-500 leading-relaxed">
                  <ShieldAlert className="w-3 h-3 mt-0.5 shrink-0 text-amber-300" />
                  <span>{disclaimer}</span>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}

const QuestionView = ({ q, value, onChange }) => {
  return (
    <div
      data-testid={`mental-question-${q.id}`}
      className="h-full overflow-y-auto px-5 sm:px-10 py-8 flex flex-col"
    >
      <div className="text-[11px] uppercase tracking-[0.25em] text-cyan-400 font-semibold mb-3">
        {q.type === "slider" ? "Rate 1 – 10" : "Choose one"}
      </div>
      <h3 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight leading-snug">
        {q.prompt}
      </h3>
      <p className="mt-2 text-xs text-zinc-500">
        Optional — you can skip and still get insights.
      </p>

      <div className="mt-8 flex-1">
        {q.type === "choice" && (
          <div className="grid sm:grid-cols-2 gap-2.5">
            {q.options.map((opt) => {
              const selected = value === opt.value;
              return (
                <button
                  key={opt.value}
                  data-testid={`mental-option-${q.id}-${opt.value}`}
                  onClick={() => onChange(opt.value)}
                  className={`text-left px-5 py-4 rounded-2xl border transition-all ${
                    selected
                      ? "bg-cyan-500/10 border-cyan-400/60 shadow-[0_0_20px_rgba(0,229,255,0.2)]"
                      : "bg-white/[0.02] border-white/10 hover:border-cyan-400/30 hover:bg-white/[0.04]"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-4 h-4 rounded-full border ${
                        selected
                          ? "border-cyan-400 bg-cyan-400 shadow-[0_0_8px_rgba(0,229,255,0.6)]"
                          : "border-white/30"
                      }`}
                    />
                    <span
                      className={`text-sm ${
                        selected ? "text-white font-medium" : "text-zinc-300"
                      }`}
                    >
                      {opt.label}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {q.type === "slider" && (
          <div className="glass-card rounded-2xl p-6">
            <div className="flex items-center justify-between text-[11px] uppercase tracking-widest text-zinc-500">
              <span>{q.labels?.min || `${q.min}`}</span>
              <span className="text-cyan-300 font-semibold text-lg tracking-normal normal-case">
                {value ?? Math.round(((q.min || 0) + (q.max || 10)) / 2)}
              </span>
              <span>{q.labels?.max || `${q.max}`}</span>
            </div>
            <input
              data-testid={`mental-slider-${q.id}`}
              type="range"
              min={q.min || 0}
              max={q.max || 10}
              step={q.step || 1}
              value={value ?? Math.round(((q.min || 0) + (q.max || 10)) / 2)}
              onChange={(e) => onChange(e.target.value)}
              className="w-full mt-4 accent-cyan-400"
            />
            <div className="mt-4 flex flex-wrap gap-1.5 justify-center">
              {Array.from(
                { length: (q.max || 10) - (q.min || 1) + 1 },
                (_, i) => (q.min || 1) + i
              ).map((n) => (
                <button
                  key={n}
                  data-testid={`mental-slider-tick-${q.id}-${n}`}
                  onClick={() => onChange(String(n))}
                  className={`w-9 h-9 rounded-lg text-xs font-medium transition-all ${
                    String(n) === (value ?? "")
                      ? "bg-cyan-500 text-white shadow-[0_0_15px_rgba(0,229,255,0.5)]"
                      : "bg-white/[0.04] text-zinc-400 hover:bg-white/[0.08]"
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ResultView = ({ assessment }) => {
  const conf = CONFIDENCE_STYLE[assessment.confidence] || CONFIDENCE_STYLE.Medium;
  const { user } = useAuth();
  const savedRef = useRef(null);
  useEffect(() => {
    // Save the result to the signed-in user's private server-side history.
    if (assessment && user && savedRef.current !== assessment) {
      savedRef.current = assessment;
      saveRecord({
        type: "mental",
        title: `Mental Wellness — ${assessment.score}/100`,
        summary: assessment.summary || "",
        score: assessment.score,
        payload: assessment,
      }).then((ok) => ok && toast.success("Saved to your private health history"));
    }
  }, [assessment, user]);
  return (
    <div
      data-testid="mental-assessment-card"
      className="h-full overflow-y-auto px-5 sm:px-10 py-8"
    >
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="text-xs font-semibold uppercase tracking-[0.25em] text-cyan-400">
          Mental Wellness Score (0–100)
        </div>
        <div
          data-testid="mental-confidence-badge"
          className={`ml-auto inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-[10px] uppercase tracking-widest font-semibold ${conf.bg} ${conf.border} ${conf.color}`}
        >
          <Info className="w-3 h-3" />
          {assessment.confidence} confidence
        </div>
        <button
          data-testid="download-mental-report-btn"
          onClick={() => {
            downloadMentalReportPDF(assessment);
            unlockAchievement("download_report", 25);
          }}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium bg-cyan-500/10 border border-cyan-400/40 text-cyan-200 hover:bg-cyan-500/20 transition-colors"
        >
          <Download className="w-3 h-3" />
          Download PDF
        </button>
      </div>

      <div className="flex flex-col md:flex-row items-start md:items-center gap-8">
        <BigMentalRing score={assessment.score} />
        <div className="flex-1">
          <div className="text-4xl sm:text-5xl font-display font-semibold">
            {assessment.score}
            <span className="text-xl text-zinc-500">/100</span>
          </div>
          <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 rounded-full glass-card text-xs text-cyan-300">
            <Sparkles className="w-3 h-3" />
            {assessment.label}
          </div>
          {assessment.summary && (
            <p className="mt-4 text-zinc-300 text-base leading-relaxed max-w-xl">
              {assessment.summary}
            </p>
          )}
        </div>
      </div>

      {/* Sub-dimensions */}
      <div className="mt-10" data-testid="mental-subscores">
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-400 mb-4">
          Wellness Dimensions
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <MiniDim label="Stress" score={assessment.stress_score} tag={assessment.stress_level} />
          <MiniDim label="Sleep" score={assessment.sleep_score} tag={assessment.sleep_quality} />
          <MiniDim label="Emotional" score={assessment.emotional_score} tag={assessment.emotional_wellbeing} />
          <MiniDim label="Lifestyle" score={assessment.lifestyle_score} tag={assessment.lifestyle_balance} />
        </div>
      </div>

      {/* Explainable AI */}
      {assessment.factors?.length > 0 && (
        <div className="mt-10" data-testid="mental-factors">
          <div className="flex items-center gap-2 mb-4">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-400">
              Why did I receive this score?
            </div>
            <div className="text-[10px] text-cyan-400 border border-cyan-400/20 rounded-full px-2 py-0.5 uppercase tracking-widest">
              Explainable AI
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {assessment.factors.map((f, i) => {
              const imp = IMPACT_STYLE[f.impact] || IMPACT_STYLE.neutral;
              const ImpactIcon = imp.Icon;
              return (
                <div
                  key={i}
                  className={`glass-card rounded-2xl p-4 border ${imp.border}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-9 h-9 rounded-xl ${imp.bg} border ${imp.border} flex items-center justify-center shrink-0`}>
                      <ImpactIcon className={`w-4 h-4 ${imp.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="text-sm font-semibold text-white">
                          {f.name}
                        </div>
                        <div className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold ${imp.color}`}>
                          {imp.label}
                        </div>
                      </div>
                      {f.value && (
                        <div className="text-xs text-zinc-500 mt-0.5">
                          You said: <span className="text-zinc-300">{f.value}</span>
                        </div>
                      )}
                      {f.detail && (
                        <p className="text-xs text-zinc-300 mt-2 leading-relaxed">
                          {f.detail}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {(assessment.positive_habits?.length || assessment.improvement_areas?.length) && (
        <div className="mt-10 grid sm:grid-cols-2 gap-3">
          {assessment.positive_habits?.length > 0 && (
            <div className="glass-card rounded-2xl p-5">
              <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-semibold mb-3">
                Positive habits
              </div>
              <ul className="space-y-2">
                {assessment.positive_habits.map((h, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-zinc-200">
                    <Check className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                    {h}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {assessment.improvement_areas?.length > 0 && (
            <div className="glass-card rounded-2xl p-5">
              <div className="text-[10px] uppercase tracking-widest text-amber-300 font-semibold mb-3">
                Areas needing improvement
              </div>
              <ul className="space-y-2">
                {assessment.improvement_areas.map((h, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-zinc-200">
                    <ArrowRight className="w-4 h-4 text-amber-300 mt-0.5 shrink-0" />
                    {h}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Recommendations */}
      {assessment.recommendations?.length > 0 && (
        <div className="mt-10" data-testid="mental-recommendations">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-400 mb-4">
            Personalized Suggestions
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {assessment.recommendations.map((r, i) => {
              const Icon = CATEGORY_ICONS[r.category] || Leaf;
              return (
                <div
                  key={i}
                  data-testid={`mental-rec-${i}`}
                  className="glass-card rounded-2xl p-4 border border-white/10"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#0B3D91]/30 to-cyan-500/10 border border-cyan-500/20 flex items-center justify-center shrink-0">
                      <Icon className="w-4 h-4 text-cyan-400" />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-white leading-snug">
                        {r.title}
                      </div>
                      {r.why && (
                        <div className="mt-2">
                          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
                            Why
                          </div>
                          <p className="text-xs text-zinc-300 mt-0.5 leading-relaxed">
                            {r.why}
                          </p>
                        </div>
                      )}
                      {r.benefit && (
                        <div className="mt-2">
                          <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-semibold">
                            Benefit
                          </div>
                          <p className="text-xs text-zinc-300 mt-0.5 leading-relaxed">
                            {r.benefit}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Instant AI Coping Box — appears when the scan suggests elevated stress */}
      {(/high|severe|elevated|very/i.test(assessment.stress_level || "") ||
        (assessment.stress_score ?? 100) <= 45 ||
        (assessment.score ?? 100) <= 45) && (
        <CopingBox stressLevel={assessment.stress_level} score={assessment.score} />
      )}

      <div
        data-testid="mental-disclaimer"
        className="mt-10 rounded-2xl border border-amber-400/20 bg-amber-500/[0.05] p-4 flex items-start gap-3"
      >
        <ShieldAlert className="w-5 h-5 text-amber-300 shrink-0 mt-0.5" />
        <p className="text-xs text-amber-100/90 leading-relaxed">
          <span className="font-semibold text-amber-200">Disclaimer:</span>{" "}
          SilentScan AI provides health and mental wellness awareness only. It
          does not diagnose physical or mental health conditions. Always consult
          qualified healthcare or mental-health professionals for diagnosis or
          treatment.
        </p>
      </div>
    </div>
  );
};

const DistressView = ({ message, helplines, onRestart }) => (
  <div
    data-testid="mental-distress-screen"
    className="h-full overflow-y-auto px-5 sm:px-10 py-10"
  >
    <div className="max-w-2xl mx-auto text-center">
      <div className="mx-auto w-20 h-20 rounded-full bg-cyan-500/15 border border-cyan-400/40 flex items-center justify-center">
        <HeartHandshake className="w-9 h-9 text-cyan-300" />
      </div>
      <div className="mt-6 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-400/30 text-[11px] uppercase tracking-[0.2em] text-cyan-300 font-semibold">
        You matter · You're not alone
      </div>
      <h3 className="mt-5 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-white">
        Please reach out to someone right now.
      </h3>
      <p className="mt-4 text-zinc-300 text-base leading-relaxed">{message}</p>

      {helplines?.length > 0 && (
        <div className="mt-8 grid sm:grid-cols-2 gap-3 text-left">
          {helplines.map((h, i) => (
            <div
              key={i}
              data-testid={`helpline-${i}`}
              className="glass-card rounded-2xl p-4 flex items-start gap-3"
            >
              <Phone className="w-5 h-5 text-cyan-400 mt-0.5" />
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
                  {h.country}
                </div>
                <div className="text-sm font-medium text-white mt-0.5">
                  {h.name}
                </div>
                <div className="text-xs text-cyan-300 mt-0.5 font-mono">
                  {h.contact}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-8 glass-card rounded-2xl p-4 flex items-start gap-3 text-left">
        <Stethoscope className="w-5 h-5 text-cyan-400 mt-0.5 shrink-0" />
        <p className="text-sm text-zinc-300 leading-relaxed">
          If you're in immediate danger, please contact your local emergency
          services or go to the nearest hospital. If you have someone you trust
          — a family member, friend, teacher, doctor — please tell them what
          you're feeling right now.
        </p>
      </div>

      <button
        data-testid="mental-distress-restart"
        onClick={onRestart}
        className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full btn-outline-glass text-white text-sm font-medium"
      >
        <RefreshCcw className="w-3.5 h-3.5" />
        Start again when you're ready
      </button>
    </div>
  </div>
);

const MiniDim = ({ label, score, tag }) => {
  const pct = Math.max(0, Math.min(100, Number(score) || 0));
  return (
    <div
      data-testid={`mental-subscore-${label.toLowerCase()}`}
      className="glass-card rounded-2xl p-4 flex flex-col items-center text-center"
    >
      <div className="relative w-16 h-16">
        <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r="42" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="10" />
          <circle
            cx="50"
            cy="50"
            r="42"
            fill="none"
            stroke="url(#mental-grad)"
            strokeWidth="10"
            strokeLinecap="round"
            strokeDasharray={`${(pct / 100) * 264} 264`}
            style={{ transition: "stroke-dasharray 1.2s ease" }}
          />
          <defs>
            <linearGradient id="mental-grad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#00E5FF" />
              <stop offset="100%" stopColor="#0B3D91" />
            </linearGradient>
          </defs>
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="font-display text-base font-semibold">{pct}</div>
        </div>
      </div>
      <div className="mt-2 text-[11px] text-zinc-400 uppercase tracking-widest">
        {label}
      </div>
      {tag && (
        <div className="mt-0.5 text-[10px] text-cyan-300">{tag}</div>
      )}
    </div>
  );
};

const BigMentalRing = ({ score }) => (
  <div className="relative w-32 h-32 sm:w-40 sm:h-40 shrink-0">
    <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
      <circle cx="50" cy="50" r="42" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="8" />
      <circle
        cx="50"
        cy="50"
        r="42"
        fill="none"
        stroke="url(#mental-big-grad)"
        strokeWidth="8"
        strokeLinecap="round"
        strokeDasharray={`${(score / 100) * 264} 264`}
        style={{ transition: "stroke-dasharray 1.2s ease" }}
      />
      <defs>
        <linearGradient id="mental-big-grad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#00E5FF" />
          <stop offset="100%" stopColor="#0B3D91" />
        </linearGradient>
      </defs>
    </svg>
    <div className="absolute inset-0 flex flex-col items-center justify-center">
      <div className="font-display text-3xl sm:text-4xl font-semibold">
        {score}
      </div>
      <div className="text-[10px] text-zinc-500 uppercase tracking-widest mt-1">
        Wellness
      </div>
    </div>
  </div>
);
